import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, Table } from 'react-bootstrap';
import { FiArrowLeft, FiCloudRain, FiPlus, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import { checkManualActivityForecast, checkManualGreenRiverForecast, lookupPolicy } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import {
    parseActivityDate,
    generateWeatherDataFromActivitySchedule,
    extractForecastScheduleErrorDetail,
    buildWeatherDataFromScheduleError,
    mergeWeatherRowValues,
    parseWeatherValue,
    formatWeatherSlotLabel,
} from '../../utils/manualForecastCheckUtils';
import { useTranslation } from '../../hooks/useTranslation';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsForecasts.css';

const buildMeteomaticsFromDate = (date) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const pad = (n) => String(n).padStart(2, '0');
    return {
        meteomatics_init_day: day,
        meteomatics_init_month: month,
        meteomatics_init_year: year,
        meteomatics_run_hour_utc: `${year}-${pad(month)}-${pad(day)}T00:00:00Z`,
        meteomatics_run_hour_paris: `${year}-${pad(month)}-${pad(day)}T02:00:00+02:00`,
    };
};

const sumWeatherValues = (rows) => rows.reduce((acc, row) => acc + parseWeatherValue(row.value), 0);

const emptyWeatherRow = () => {
    const now = new Date();
    return {
        forecast_hour: '08:00:00',
        forecast_day: now.getDate(),
        forecast_month: now.getMonth() + 1,
        forecast_year: now.getFullYear(),
        value: 0,
    };
};

const MANUAL_FORECAST_PROVIDERS = {
    activity: {
        submitForecast: checkManualActivityForecast,
    },
    greenRiver: {
        submitForecast: checkManualGreenRiverForecast,
    },
};

const ManualForecastCheckPage = ({ provider = 'activity' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const providerConfig = MANUAL_FORECAST_PROVIDERS[provider] || MANUAL_FORECAST_PROVIDERS.activity;
    const translationNs = provider === 'greenRiver'
        ? 'pages.activityForecasts.greenRiverManualCheck'
        : 'pages.activityForecasts.manualCheck';

    const tc = useCallback(
        (key) => t(`${translationNs}.${key}`),
        [t, translationNs]
    );

    const initialState = location.state || {};

    const [policyCode, setPolicyCode] = useState(
        initialState.policy_code || searchParams.get('policy_code') || ''
    );
    const [reservationNumber, setReservationNumber] = useState(
        initialState.reservation_number || searchParams.get('reservation_number') || ''
    );
    const [activityStartDate, setActivityStartDate] = useState(initialState.activity_start_date || '');
    const [activityEndDate, setActivityEndDate] = useState(initialState.activity_end_date || '');
    const [activityStartTime, setActivityStartTime] = useState(initialState.activity_start_time || '');
    const [activityEndTime, setActivityEndTime] = useState(initialState.activity_end_time || '');
    const [weatherData, setWeatherData] = useState(() =>
        generateWeatherDataFromActivitySchedule(
            initialState.activity_start_date,
            initialState.activity_end_date,
            initialState.activity_start_time,
            initialState.activity_end_time
        )
    );
    const [meteomatics, setMeteomatics] = useState(() => {
        const start = parseActivityDate(initialState.activity_start_date) || new Date();
        return buildMeteomaticsFromDate(start);
    });
    const [totalMmPrc, setTotalMmPrc] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [scheduleError, setScheduleError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState(null);
    const [policyLookupInfo, setPolicyLookupInfo] = useState(null);
    const lastLookupKeyRef = useRef('');
    const lookupRequestIdRef = useRef(0);

    const computedTotal = useMemo(() => sumWeatherValues(weatherData), [weatherData]);

    useEffect(() => {
        setTotalMmPrc(Number(computedTotal.toFixed(2)));
    }, [computedTotal]);

    const applyActivityScheduleToForm = useCallback((startDate, endDate, startTime, endTime, { preserveValues = false } = {}) => {
        const start = parseActivityDate(startDate);
        if (start) {
            setMeteomatics(buildMeteomaticsFromDate(start));
        }
        setWeatherData((prev) => {
            const newRows = generateWeatherDataFromActivitySchedule(startDate, endDate, startTime, endTime);
            return preserveValues ? mergeWeatherRowValues(newRows, prev) : newRows;
        });
    }, []);

    const applyPolicyLookupResult = useCallback((data, lookupKey) => {
        const policiesInfo = data?.policies_info || data;
        const activity = policiesInfo?.activity_info;
        if (!activity) {
            return false;
        }

        setPolicyLookupInfo(policiesInfo);
        const startDate = activity.activity_start_date || '';
        const endDate = activity.activity_end_date || startDate;
        const startTime = activity.activity_start_time || '';
        const endTime = activity.activity_end_time || '';

        setActivityStartDate(startDate);
        setActivityEndDate(endDate);
        setActivityStartTime(startTime);
        setActivityEndTime(endTime);
        applyActivityScheduleToForm(startDate, endDate, startTime, endTime, { preserveValues: true });
        if (lookupKey) {
            lastLookupKeyRef.current = lookupKey;
        }
        return true;
    }, [applyActivityScheduleToForm]);

    const fetchPolicyLookup = useCallback(async (code, reservation, { force = false } = {}) => {
        const trimmedCode = code?.trim();
        const trimmedReservation = reservation?.trim();
        if (!trimmedCode || !trimmedReservation) {
            setPolicyLookupInfo(null);
            setLookupError(null);
            lastLookupKeyRef.current = '';
            return;
        }

        const lookupKey = `${trimmedCode}|${trimmedReservation}`;
        if (!force && lastLookupKeyRef.current === lookupKey) {
            return;
        }

        const requestId = lookupRequestIdRef.current + 1;
        lookupRequestIdRef.current = requestId;

        try {
            setLookupLoading(true);
            setLookupError(null);
            const data = await lookupPolicy({
                policy_code: trimmedCode,
                reservation_number: trimmedReservation,
            });
            if (lookupRequestIdRef.current !== requestId) {
                return;
            }
            const applied = applyPolicyLookupResult(data, lookupKey);
            if (!applied) {
                setLookupError(tc('lookupNoActivity'));
                setPolicyLookupInfo(null);
                lastLookupKeyRef.current = '';
            }
        } catch (err) {
            if (lookupRequestIdRef.current !== requestId) {
                return;
            }
            setPolicyLookupInfo(null);
            lastLookupKeyRef.current = '';
            setLookupError(handleApiError(err, tc('lookupError')));
        } finally {
            if (lookupRequestIdRef.current === requestId) {
                setLookupLoading(false);
            }
        }
    }, [applyPolicyLookupResult, t]);

    useEffect(() => {
        if (initialState.activity_start_date && !initialState.policy_code) {
            applyActivityScheduleToForm(
                initialState.activity_start_date,
                initialState.activity_end_date,
                initialState.activity_start_time,
                initialState.activity_end_time
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const trimmedCode = policyCode.trim();
        const trimmedReservation = reservationNumber.trim();
        if (!trimmedCode || !trimmedReservation) {
            lastLookupKeyRef.current = '';
            return undefined;
        }

        const lookupKey = `${trimmedCode}|${trimmedReservation}`;
        if (lastLookupKeyRef.current === lookupKey) {
            return undefined;
        }

        const timer = setTimeout(() => {
            fetchPolicyLookup(trimmedCode, trimmedReservation);
        }, 600);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policyCode, reservationNumber]);

    const handleFixScheduleFromError = () => {
        if (!scheduleError) return;
        if (scheduleError.activity_start_date) setActivityStartDate(scheduleError.activity_start_date);
        if (scheduleError.activity_end_date) setActivityEndDate(scheduleError.activity_end_date);
        if (scheduleError.activity_start_time) setActivityStartTime(scheduleError.activity_start_time);
        if (scheduleError.activity_end_time) setActivityEndTime(scheduleError.activity_end_time);
        setWeatherData(buildWeatherDataFromScheduleError(scheduleError));
        setScheduleError(null);
        setError(null);
    };

    const handleWeatherRowChange = (index, field, value) => {
        setWeatherData((prev) =>
            prev.map((row, i) => {
                if (i !== index) return row;
                if (field === 'value') {
                    return { ...row, value };
                }
                if (['forecast_day', 'forecast_month', 'forecast_year'].includes(field)) {
                    return { ...row, [field]: value === '' ? '' : Number(value) };
                }
                return { ...row, [field]: value };
            })
        );
    };

    const handleWeatherValueChange = (index, rawValue) => {
        const normalized = rawValue.replace(',', '.');
        if (normalized !== '' && !/^\d*\.?\d*$/.test(normalized)) {
            return;
        }
        handleWeatherRowChange(index, 'value', normalized);
    };

    const handleAddWeatherRow = () => {
        const ref = weatherData[weatherData.length - 1] || emptyWeatherRow();
        setWeatherData((prev) => [
            ...prev,
            {
                forecast_hour: ref.forecast_hour,
                forecast_day: ref.forecast_day,
                forecast_month: ref.forecast_month,
                forecast_year: ref.forecast_year,
                value: 0,
            },
        ]);
    };

    const handleRemoveWeatherRow = (index) => {
        setWeatherData((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    const handleRegenerateFromActivityDates = () => {
        applyActivityScheduleToForm(
            activityStartDate,
            activityEndDate || activityStartDate,
            activityStartTime,
            activityEndTime
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setScheduleError(null);
        setSuccess(null);

        if (!policyCode.trim() && !reservationNumber.trim()) {
            setError(tc('policyRequired'));
            return;
        }

        const payload = {
            weather_data: weatherData.map((row) => ({
                forecast_hour: row.forecast_hour,
                forecast_day: Number(row.forecast_day),
                forecast_month: Number(row.forecast_month),
                forecast_year: Number(row.forecast_year),
                value: parseWeatherValue(row.value),
            })),
            weather_temp: [],
            weather_wind: [],
            ...meteomatics,
            total_mm_prc: Number(totalMmPrc) || computedTotal,
            data_source: 'manual',
        };

        try {
            setLoading(true);
            const result = await providerConfig.submitForecast(payload, {
                policy_code: policyCode.trim() || undefined,
                reservation_number: reservationNumber.trim() || undefined,
            });
            setSuccess(
                result?.message
                || tc('success')
            );
        } catch (err) {
            const scheduleDetail = extractForecastScheduleErrorDetail(err);
            if (scheduleDetail) {
                setScheduleError(scheduleDetail);
                setError(scheduleDetail.message || tc('scheduleMismatch'));
            } else {
                setError(handleApiError(err, tc('error')));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-page admin-trips-forecasts-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                        <div>
                            <h2 className="page-title">
                                <FiCloudRain className="me-2" />
                                {tc('title')}
                            </h2>
                            <p className="text-muted mb-0">{tc('subtitle')}</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/activity/forecasts')}>
                            <FiArrowLeft className="me-2" />
                            {t('common.back')}
                        </Button>
                    </div>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => { setError(null); setScheduleError(null); }}>
                            <strong>{error}</strong>
                            {scheduleError && (
                                <div className="mt-3 small">
                                    {(scheduleError.activity_start_date || scheduleError.activity_start_time) && (
                                        <p className="mb-2">
                                            <strong>{tc('activitySchedule')} :</strong>{' '}
                                            {scheduleError.activity_start_date} {scheduleError.activity_start_time}
                                            {' → '}
                                            {scheduleError.activity_end_date} {scheduleError.activity_end_time}
                                        </p>
                                    )}
                                    {Array.isArray(scheduleError.expected_forecast_hours) && scheduleError.expected_forecast_hours.length > 0 && (
                                        <p className="mb-2">
                                            <strong>{tc('expectedHours')} :</strong>{' '}
                                            {scheduleError.expected_forecast_hours.join(', ')}
                                        </p>
                                    )}
                                    {Array.isArray(scheduleError.missing_weather_data_slots) && scheduleError.missing_weather_data_slots.length > 0 && (
                                        <div className="mb-2">
                                            <strong>{tc('missingSlots')} :</strong>
                                            <ul className="mb-0 mt-1">
                                                {scheduleError.missing_weather_data_slots.map((slot, idx) => (
                                                    <li key={`missing-${idx}`}>{formatWeatherSlotLabel(slot)}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {Array.isArray(scheduleError.unexpected_weather_data_slots) && scheduleError.unexpected_weather_data_slots.length > 0 && (
                                        <div className="mb-2">
                                            <strong>{tc('unexpectedSlots')} :</strong>
                                            <ul className="mb-0 mt-1">
                                                {scheduleError.unexpected_weather_data_slots.map((slot, idx) => (
                                                    <li key={`unexpected-${idx}`}>{formatWeatherSlotLabel(slot)}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <Button
                                        type="button"
                                        variant="outline-danger"
                                        size="sm"
                                        className="mt-2"
                                        onClick={handleFixScheduleFromError}
                                    >
                                        <FiRefreshCw className="me-1" />
                                        {tc('fixSlots')}
                                    </Button>
                                </div>
                            )}
                        </Alert>
                    )}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        <Card className="mb-3">
                            <Card.Header>{tc('policySection')}</Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={5}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('pages.activityForecasts.filters.policyCode')}</Form.Label>
                                            <Form.Control
                                                value={policyCode}
                                                onChange={(e) => setPolicyCode(e.target.value)}
                                                placeholder="Ex: 317390621"
                                                disabled={lookupLoading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={5}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('pages.activityForecasts.filters.reservationNumber')}</Form.Label>
                                            <Form.Control
                                                value={reservationNumber}
                                                onChange={(e) => setReservationNumber(e.target.value)}
                                                placeholder="Ex: RSW838393003"
                                                disabled={lookupLoading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} className="d-flex align-items-end">
                                        <Form.Group className="mb-3 w-100">
                                            <Button
                                                type="button"
                                                variant="outline-primary"
                                                className="w-100"
                                                disabled={lookupLoading || !policyCode.trim() || !reservationNumber.trim()}
                                                onClick={() => fetchPolicyLookup(policyCode, reservationNumber, { force: true })}
                                            >
                                                {lookupLoading ? (
                                                    <Spinner size="sm" animation="border" />
                                                ) : (
                                                    <FiRefreshCw />
                                                )}
                                            </Button>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Text className="text-muted d-block mb-2">
                                    {tc('lookupHint')}
                                </Form.Text>
                                {lookupLoading && (
                                    <div className="text-muted small mb-2">
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        {tc('lookupLoading')}
                                    </div>
                                )}
                                {lookupError && (
                                    <Alert variant="warning" className="py-2 mb-2">
                                        {lookupError}
                                    </Alert>
                                )}
                                {policyLookupInfo?.activity_info && (
                                    <Alert variant="info" className="py-2 mb-0">
                                        <div className="fw-semibold mb-1">
                                            {policyLookupInfo.activity_info.name || '—'}
                                            {policyLookupInfo.partner_info?.company_name && (
                                                <span className="text-muted fw-normal">
                                                    {' '}({policyLookupInfo.partner_info.company_name})
                                                </span>
                                            )}
                                        </div>
                                        {policyLookupInfo.client_info && (
                                            <div className="small mb-1">
                                                {policyLookupInfo.client_info.first_name} {policyLookupInfo.client_info.last_name}
                                            </div>
                                        )}
                                        <div className="small">
                                            <strong>{t('pages.activityForecasts.table.startDate')} :</strong>{' '}
                                            {policyLookupInfo.activity_info.activity_start_date}{' '}
                                            {policyLookupInfo.activity_info.activity_start_time}
                                            <br />
                                            <strong>{t('pages.activityForecasts.table.endDate')} :</strong>{' '}
                                            {policyLookupInfo.activity_info.activity_end_date}{' '}
                                            {policyLookupInfo.activity_info.activity_end_time}
                                        </div>
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>

                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <span>{tc('activityDatesSection')}</span>
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleRegenerateFromActivityDates}
                                >
                                    <FiRefreshCw className="me-1" />
                                    {tc('regenerateSlots')}
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('pages.activityForecasts.table.startDate')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={activityStartDate}
                                                onChange={(e) => setActivityStartDate(e.target.value)}
                                                placeholder="2026-05-19"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{tc('startTime')}</Form.Label>
                                            <Form.Control
                                                type="time"
                                                step={3600}
                                                value={activityStartTime ? activityStartTime.slice(0, 5) : ''}
                                                onChange={(e) => setActivityStartTime(e.target.value ? `${e.target.value}:00` : '')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('pages.activityForecasts.table.endDate')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={activityEndDate}
                                                onChange={(e) => setActivityEndDate(e.target.value)}
                                                placeholder="2026-05-19"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{tc('endTime')}</Form.Label>
                                            <Form.Control
                                                type="time"
                                                step={3600}
                                                value={activityEndTime ? activityEndTime.slice(0, 5) : ''}
                                                onChange={(e) => setActivityEndTime(e.target.value ? `${e.target.value}:00` : '')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Text className="text-muted">
                                    {tc('activityDatesHint')}
                                </Form.Text>
                            </Card.Body>
                        </Card>

                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <span>{tc('weatherDataSection')}</span>
                                <Button type="button" variant="outline-primary" size="sm" onClick={handleAddWeatherRow}>
                                    <FiPlus className="me-1" />
                                    {tc('addHour')}
                                </Button>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table striped hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>{tc('hour')}</th>
                                                <th>{tc('day')}</th>
                                                <th>{tc('month')}</th>
                                                <th>{tc('year')}</th>
                                                <th>{tc('valueMm')}</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {weatherData.map((row, index) => (
                                                <tr key={`weather-row-${row.forecast_year}-${row.forecast_month}-${row.forecast_day}-${row.forecast_hour}-${index}`}>
                                                    <td>
                                                        <Form.Control
                                                            type="time"
                                                            step={3600}
                                                            value={row.forecast_hour?.slice(0, 5) || '08:00'}
                                                            onChange={(e) => {
                                                                const [h, m] = (e.target.value || '08:00').split(':');
                                                                handleWeatherRowChange(
                                                                    index,
                                                                    'forecast_hour',
                                                                    `${h || '08'}:${m || '00'}:00`
                                                                );
                                                            }}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={1}
                                                            max={31}
                                                            value={row.forecast_day}
                                                            onChange={(e) => handleWeatherRowChange(index, 'forecast_day', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={1}
                                                            max={12}
                                                            value={row.forecast_month}
                                                            onChange={(e) => handleWeatherRowChange(index, 'forecast_month', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={2000}
                                                            max={2100}
                                                            value={row.forecast_year}
                                                            onChange={(e) => handleWeatherRowChange(index, 'forecast_year', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={row.value ?? ''}
                                                            onChange={(e) => handleWeatherValueChange(index, e.target.value)}
                                                            placeholder="0"
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Button
                                                            type="button"
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveWeatherRow(index)}
                                                            disabled={weatherData.length <= 1}
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="mb-3">
                            <Card.Header>{tc('meteomaticsSection')}</Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>meteomatics_init_day</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={meteomatics.meteomatics_init_day}
                                                onChange={(e) => setMeteomatics((prev) => ({
                                                    ...prev,
                                                    meteomatics_init_day: Number(e.target.value),
                                                }))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>meteomatics_init_month</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={meteomatics.meteomatics_init_month}
                                                onChange={(e) => setMeteomatics((prev) => ({
                                                    ...prev,
                                                    meteomatics_init_month: Number(e.target.value),
                                                }))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>meteomatics_init_year</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={meteomatics.meteomatics_init_year}
                                                onChange={(e) => setMeteomatics((prev) => ({
                                                    ...prev,
                                                    meteomatics_init_year: Number(e.target.value),
                                                }))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>meteomatics_run_hour_utc</Form.Label>
                                            <Form.Control
                                                value={meteomatics.meteomatics_run_hour_utc}
                                                onChange={(e) => setMeteomatics((prev) => ({
                                                    ...prev,
                                                    meteomatics_run_hour_utc: e.target.value,
                                                }))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>meteomatics_run_hour_paris</Form.Label>
                                            <Form.Control
                                                value={meteomatics.meteomatics_run_hour_paris}
                                                onChange={(e) => setMeteomatics((prev) => ({
                                                    ...prev,
                                                    meteomatics_run_hour_paris: e.target.value,
                                                }))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('pages.activityForecasts.table.precipitation')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.1"
                                                value={totalMmPrc}
                                                onChange={(e) => setTotalMmPrc(e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                {tc('totalAuto')}: {computedTotal.toFixed(2)} mm
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>data_source</Form.Label>
                                            <Form.Control value="manual" disabled readOnly />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end gap-2">
                            <Button type="button" variant="outline-secondary" onClick={() => navigate('/activity/forecasts')}>
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {tc('submitting')}
                                    </>
                                ) : (
                                    tc('submit')
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

const AdminActivityManualForecastCheck = () => (
    <ManualForecastCheckPage provider="activity" />
);

export const AdminGreenRiverManualForecastCheck = () => (
    <ManualForecastCheckPage provider="greenRiver" />
);

export default AdminActivityManualForecastCheck;

